import { useState, useEffect, useRef, useCallback } from "react";
import { $host } from "../../../../http";
import ReactPlayer from "react-player";

const SelectWebinarModal = ({ isOpen, onClose, onSelect, themeId, onUpdate }) => {
    const [webinars, setWebinars] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [updatedAfter, setUpdatedAfter] = useState("");
    const [playingWebinar, setPlayingWebinar] = useState(null);
    const [selectedWebinar, setSelectedWebinar] = useState(null);
    const abortController = useRef(new AbortController());

    useEffect(() => {
        if (isOpen) {
            abortController.current = new AbortController();
            fetchWebinars();
        } else {
            abortController.current.abort();
        }
    }, [isOpen, page, updatedAfter]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, onClose]);

    const fetchWebinars = async () => {
        setLoading(true);
        try {
            const response = await $host.get("/api/teachers/webinars-records", {
                params: { page, limit: 10, updated_after: updatedAfter },
                withCredentials: true,
                signal: abortController.current.signal,
            });

            setWebinars(response.data.records || []);
            setTotalPages(response.data.totalPages || 1);
        } catch (err) {
            if (err.name !== "AbortError") {
                setError(err.response?.data?.error || "Ошибка загрузки вебинаров");
            }
        }
        setLoading(false);
    };

    const confirmSelection = async () => {
        if (!selectedWebinar || !selectedWebinar.id) return;

        try {
            const response = await $host.put(`/api/teachers/themes/${themeId}`, {
                webinar_room_id: selectedWebinar.id,
                webinar_url: selectedWebinar.link_recording,
            }, { withCredentials: true });

            onUpdate(themeId, response.data);
            onSelect(selectedWebinar);
            onClose();
        } catch (error) {
            console.error("Ошибка обновления темы:", error.response?.data?.message || error.message);
        } finally {
            setSelectedWebinar(null);
        }
    };

    const handleSelect = (webinar) => {
        setSelectedWebinar(webinar);
    };

    const formatDateRFC3339 = (dateString) => {
        const date = new Date(dateString);
        return date.toISOString();
    };

    const handlePageChange = (e) => {
        const newPage = Number(e.target.value);
        if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-[900px] max-h-[95vh] overflow-auto relative">
                {/* 🔥 Кнопка закрытия модального окна */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                >
                    ❌
                </button>

                <h2 className="text-xl font-bold mb-4 text-gray-800">Выберите вебинар</h2>

                {/* Фильтр по дате */}
                <div className="mb-4 flex items-center gap-4">
                    <label className="block text-sm font-medium text-gray-700">Фильтр по дате:</label>
                    <input
                        type="datetime-local"
                        value={updatedAfter}
                        onChange={(e) => setUpdatedAfter(formatDateRFC3339(e.target.value))}
                        className="p-2 border rounded-md flex-1"
                    />
                </div>

                {loading && <p className="text-center text-gray-500">Загрузка...</p>}
                {error && <p className="text-red-500">{error}</p>}

                {/* Грид с вебинарами */}
                <div className="grid grid-cols-2 gap-4">
                    {webinars.length === 0 && !loading && (
                        <p className="text-center col-span-2 text-gray-500">Нет доступных вебинаров</p>
                    )}

                    {webinars.map((webinar) => (
                        <div
                            key={webinar.id}
                            className="border p-2 rounded-lg shadow-md cursor-pointer hover:bg-gray-100 relative"
                            onClick={() => handleSelect(webinar)}
                        >
                            {/* Видео превью */}
                            <div className="relative w-full h-40 bg-gray-200 rounded-xl">
                                {webinar.video_preview ? (
                                    <img
                                        src={webinar.video_preview}
                                        alt="Превью"
                                        className="w-full h-full object-cover rounded-xl"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-300 rounded-xl">
                                        <span className="text-gray-500 text-sm">Нет превью</span>
                                    </div>
                                )}

                                {/* 🔥 Кнопка просмотра видео */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPlayingWebinar(webinar.link_recording);
                                    }}
                                    className="absolute bottom-2 right-2 bg-white text-lavender-700 px-3 py-1 rounded-full text-xs font-semibold shadow"
                                >
                                    ▶️ Воспроизвести
                                </button>
                            </div>
                            <p className="text-center mt-2 text-sm text-gray-700">{webinar.name || "Без названия"}</p>
                            <p className="text-center text-xs text-gray-500">Создан: {new Date(webinar.created_at).toLocaleDateString()}</p>
                        </div>
                    ))}
                </div>

                {/* Пагинация */}
                <div className="flex justify-between items-center mt-4">
                    <button
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                        className="px-4 py-2 bg-gray-200 rounded-md"
                        disabled={page === 1}
                    >
                        Назад
                    </button>
                    <span className="text-gray-700">Страница</span>
                    <input
                        type="number"
                        value={page}
                        onChange={handlePageChange}
                        min="1"
                        max={totalPages}
                        className="w-16 p-2 border rounded-md text-center"
                    />
                    <span className="text-gray-700">из {totalPages}</span>
                    <button
                        onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                        className="px-4 py-2 bg-gray-200 rounded-md"
                        disabled={page === totalPages}
                    >
                        Вперед
                    </button>
                </div>

                {/* 🔥 Подтверждение выбора вебинара */}
                {selectedWebinar && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded-lg shadow-xl w-[400px]">
                            <h2 className="text-lg font-semibold mb-4 text-gray-800">Подтвердите выбор вебинара</h2>
                            <p className="text-sm text-gray-700">Вы уверены, что хотите выбрать <strong>{selectedWebinar.name}</strong>?</p>
                            <div className="flex justify-end mt-4 space-x-2">
                                <button
                                    onClick={() => setSelectedWebinar(null)}
                                    className="px-4 py-2 bg-gray-300 rounded-md"
                                >
                                    Отмена
                                </button>
                                <button
                                    onClick={confirmSelection}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md"
                                >
                                    Подтвердить
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SelectWebinarModal;
